import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LogoWhite from '../../assets/LogoWhite.svg';
import UserOutlineIcon from '../icons/UserOutlineIcon';
import styles from './AdminNavbar.module.scss';
const AdminNavbar = () => {
    const { logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [openNavGroup, setOpenNavGroup] = useState(null);
    const dropdownRef = useRef(null);
    const navGroupsRef = useRef(null);
    const closeNavGroupTimeoutRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            if (navGroupsRef.current && !navGroupsRef.current.contains(event.target)) {
                setOpenNavGroup(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            if (closeNavGroupTimeoutRef.current) {
                window.clearTimeout(closeNavGroupTimeoutRef.current);
            }
        };
    }, []);
    const handleLogout = async () => {
        await logout();
        navigate('/account/login');
    };
    const navGroups = [
        {
            label: 'Orders',
            links: [
                { href: '/web-admin/orders', label: 'Orders', matchPrefixes: ['/web-admin/orders'] },
                { href: '/web-admin/order-batching', label: 'Order Batching', matchPrefixes: ['/web-admin/order-batching'] },
            ],
        },
        {
            label: 'Accounts',
            links: [
                { href: '/web-admin/clinics', label: 'Clinics', matchPrefixes: ['/web-admin/clinics'] },
                { href: '/web-admin/clinic-users', label: 'Clinic Users', matchPrefixes: ['/web-admin/clinic-users'] },
                { href: '/web-admin/users', label: 'Users', matchPrefixes: ['/web-admin/users'] },
                { href: '/web-admin/sales-reps', label: 'Sales Reps', matchPrefixes: ['/web-admin/sales-reps'] },
            ],
        },
        {
            label: 'Catalog',
            links: [
                { href: '/web-admin/products', label: 'Products', matchPrefixes: ['/web-admin/products'] },
                { href: '/web-admin/product-categories', label: 'Product Categories', matchPrefixes: ['/web-admin/product-categories'] },
                { href: '/web-admin/shipping-methods', label: 'Shipping Methods', matchPrefixes: ['/web-admin/shipping-methods'] },
                { href: '/web-admin/manufacturer-shipping', label: 'Manufacturer Shipping', matchPrefixes: ['/web-admin/manufacturer-shipping'] },
                { href: '/web-admin/corporate-groups', label: 'Corporate Groups', matchPrefixes: ['/web-admin/corporate-groups'] },
            ],
        },
        {
            label: 'Promotions',
            links: [
                { href: '/web-admin/promo-codes', label: 'Promo Codes', matchPrefixes: ['/web-admin/promo-codes'] },
                { href: '/web-admin/promo-code-usages', label: 'Promo Code Usages', matchPrefixes: ['/web-admin/promo-code-usages'] },
            ],
        },
        {
            label: 'Content',
            links: [
                { href: '/web-admin/bio-page', label: 'Bio Page', matchPrefixes: ['/web-admin/bio-page'] },
            ],
        },
    ];
    const isPathActive = (matchPrefixes) => matchPrefixes.some((prefix) => location.pathname.startsWith(prefix));
    const isGroupActive = (group) => group.links.some((link) => isPathActive(link.matchPrefixes));
    const clearNavGroupCloseTimeout = () => {
        if (closeNavGroupTimeoutRef.current) {
            window.clearTimeout(closeNavGroupTimeoutRef.current);
            closeNavGroupTimeoutRef.current = null;
        }
    };
    const openGroupOnHover = (groupLabel) => {
        clearNavGroupCloseTimeout();
        setOpenNavGroup(groupLabel);
    };
    const scheduleNavGroupClose = () => {
        clearNavGroupCloseTimeout();
        closeNavGroupTimeoutRef.current = window.setTimeout(() => {
            setOpenNavGroup(null);
            closeNavGroupTimeoutRef.current = null;
        }, 160);
    };
    const toggleNavGroup = (groupLabel) => {
        setOpenNavGroup((current) => (current === groupLabel ? null : groupLabel));
    };
    return (_jsx("nav", { className: styles.adminNavbar, children: _jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.leftSection, children: [_jsx(Link, { to: "/", className: styles.logoLink, children: _jsx("img", { src: LogoWhite, alt: "Endurance", className: styles.logo }) }), _jsx("span", { className: styles.adminText, children: "Admin" })] }), _jsx("div", { className: styles.centerSection, children: _jsx("div", { className: styles.navGroups, ref: navGroupsRef, children: navGroups.map((group) => (_jsxs("div", { className: `${styles.navGroup} ${openNavGroup === group.label ? styles.navGroupOpen : ''}`, onMouseEnter: () => openGroupOnHover(group.label), onMouseLeave: scheduleNavGroupClose, children: [_jsx("button", { type: "button", className: `${styles.navGroupTrigger} ${isGroupActive(group) ? styles.groupActive : ''}`, onClick: () => toggleNavGroup(group.label), "aria-expanded": openNavGroup === group.label, children: group.label }), _jsx("div", { className: styles.navGroupMenu, children: group.links.map((link) => (_jsx(Link, { to: link.href, className: `${styles.navGroupLink} ${isPathActive(link.matchPrefixes) ? styles.active : ''}`, onClick: () => setOpenNavGroup(null), children: link.label }, link.href))) })] }, group.label))) }) }), _jsx("div", { className: styles.rightSection, children: _jsxs("div", { className: styles.userDropdown, ref: dropdownRef, children: [_jsx("div", { className: styles.userButton, onClick: () => setIsDropdownOpen(!isDropdownOpen), children: _jsx(UserOutlineIcon, { className: styles.userIcon, color: "white" }) }), isDropdownOpen && (_jsxs("div", { className: styles.dropdownMenu, children: [_jsx("button", { className: styles.dropdownItem, onClick: () => navigate('/web-admin/users'), children: "Admin Account" }), _jsx("button", { className: styles.dropdownItem, onClick: () => navigate('/web-admin/users'), children: "User Management" }), _jsx("button", { className: styles.dropdownItem, onClick: () => {
                                            window.location.href = '/admin/';
                                        }, children: "Django Admin" }), _jsx("button", { className: styles.dropdownItem, onClick: () => {
                                            window.location.href = '/';
                                        }, children: "Back to Full Site" }), _jsx("button", { className: styles.dropdownItem, onClick: handleLogout, children: "Logout" })] }))] }) })] }) }));
};
export default AdminNavbar;
